// Importaciones necesarias
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Button,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  CircularProgress,
  Divider,
  Chip,
  Avatar
} from '@mui/material';
import {
  AccessTime as AccessTimeIcon,
  CheckCircle as CheckCircleIcon,
  RadioButtonChecked as RadioButtonCheckedIcon
} from '@mui/icons-material';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import config from '../config';
import { useConfigEmpresa } from '../context/ConfigEmpresaContext';
import { useEstados } from '../context/EstadosContext';
import { useEmpresa } from '../context/EmpresaContext';

// Componente mejorado para Timeline con TRACKEA design
const HistorialItem = ({ log, isLast, duracion, obtenerColorEstado, simboloMoneda = 'S/' }) => {
  // Obtener color del estado desde el context
  const colorEstado = obtenerColorEstado(log.id_estado_servicio) || '#78909C';

  // Calcular tiempo relativo (zona horaria local Perú)
  const calcularTiempoRelativo = (fecha) => {
    const ahora = new Date();
    // Parsear fecha asumiendo que viene en formato ISO con zona horaria
    const fechaLog = new Date(fecha);
    const diffMs = ahora - fechaLog;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHoras = Math.floor(diffMs / 3600000);
    const diffDias = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Hace un momento';
    if (diffMins < 60) return `Hace ${diffMins} min`;
    if (diffHoras < 24) return `Hace ${diffHoras}h`;
    if (diffDias < 7) return `Hace ${diffDias}d`;
    return fechaLog.toLocaleDateString('es-PE');
  };

  return (
    <Box sx={{ position: 'relative', pb: isLast ? 0 : 4 }}>
      {/* Línea vertical conectora con gradiente */}
      {!isLast && (
        <Box
          sx={{
            position: 'absolute',
            left: '19px',
            top: '40px',
            bottom: 0,
            width: '3px',
            background: `linear-gradient(180deg, ${colorEstado} 0%, #E0E0E0 100%)`,
            borderRadius: '2px'
          }}
        />
      )}

      {/* Contenedor principal */}
      <Box sx={{ display: 'flex', gap: 2.5, alignItems: 'flex-start' }}>
        {/* Icono circular del estado con sombra */}
        <Avatar
          sx={{
            width: 40,
            height: 40,
            bgcolor: colorEstado,
            boxShadow: `0 4px 12px ${colorEstado}40`,
            border: '3px solid white',
            flexShrink: 0
          }}
        >
          {isLast ? (
            <RadioButtonCheckedIcon sx={{ fontSize: 20 }} />
          ) : (
            <CheckCircleIcon sx={{ fontSize: 20 }} />
          )}
        </Avatar>

        {/* Contenido del timeline item */}
        <Box sx={{ flex: 1 }}>
          {/* Card con info del estado */}
          <Paper
            elevation={2}
            sx={{
              p: 2.5,
              borderRadius: 3,
              borderLeft: `4px solid ${colorEstado}`,
              background: 'linear-gradient(135deg, #FFFFFF 0%, #F5F5F5 100%)',
              transition: 'all 0.3s ease',
              '&:hover': {
                boxShadow: `0 8px 24px ${colorEstado}20`,
                transform: 'translateX(4px)'
              }
            }}
          >
            {/* Nombre del estado con chip */}
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={1.5}>
              <Chip
                label={log.nombre_estado_mostrar || log.descripcion_estado}
                sx={{
                  bgcolor: colorEstado,
                  color: 'white',
                  fontWeight: 'bold',
                  fontSize: '0.875rem',
                  height: 28,
                  boxShadow: `0 2px 8px ${colorEstado}40`
                }}
              />

              {/* Duración en el estado */}
              {duracion && (
                <Chip
                  icon={<AccessTimeIcon sx={{ fontSize: 16 }} />}
                  label={duracion}
                  size="small"
                  variant="outlined"
                  sx={{
                    borderColor: colorEstado,
                    color: colorEstado,
                    fontWeight: 600
                  }}
                />
              )}
            </Box>

            {/* Fecha y hora */}
            <Box display="flex" alignItems="center" gap={1} mb={1}>
              <Typography variant="body2" fontWeight="600" color="text.primary">
                {new Date(log.fecha_real).toLocaleString('es-PE', {
                  weekday: 'short',
                  year: 'numeric',
                  month: 'short',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                • {calcularTiempoRelativo(log.fecha_real)}
              </Typography>
            </Box>

            {/* Glosa/comentarios */}
            {log.glosa && (
              <Paper
                variant="outlined"
                sx={{
                  p: 1.5,
                  mt: 1.5,
                  bgcolor: '#FAFAFA',
                  borderRadius: 2,
                  borderColor: '#E0E0E0'
                }}
              >
                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                  "{log.glosa}"
                </Typography>
              </Paper>
            )}

            {/* Imagen adjunta */}
            {log.imagen_url && (
              <Box mt={2}>
                <img
                  src={`${config.API_BASE_URL}${log.imagen_url}`}
                  alt="Evidencia del estado"
                  style={{
                    maxWidth: '100%',
                    maxHeight: 300,
                    borderRadius: 12,
                    boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
                    border: '2px solid #E0E0E0'
                  }}
                  onError={(e) => {
                    console.error('Error cargando imagen:', log.imagen_url);
                    e.target.style.display = 'none';
                  }}
                />
              </Box>
            )}

            {/* Campos Dinámicos */}
            {log.campos_dinamicos && log.campos_dinamicos.length > 0 && (
              <Box mt={2}>
                <Divider sx={{ my: 1.5 }} />
                <Typography variant="subtitle2" color="primary" fontWeight="700" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CheckCircleIcon sx={{ fontSize: 18 }} />
                  Información Capturada
                </Typography>
                <Grid container spacing={2}>
                  {log.campos_dinamicos.map((campo, idx) => {
                    const esImagen = campo.tipo_campo === 'imagen' || campo.tipo_campo === 'archivo';
                    const esTextoLargo = campo.tipo_campo === 'texto_largo';

                    return (
                      <Grid item xs={12} sm={esImagen ? 12 : esTextoLargo ? 12 : 6} key={idx}>
                        <Paper
                          elevation={1}
                          sx={{
                            p: 2,
                            bgcolor: 'white',
                            borderRadius: 2,
                            borderLeft: `3px solid ${colorEstado}`,
                            transition: 'all 0.2s',
                            '&:hover': {
                              boxShadow: 3
                            }
                          }}
                        >
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{
                              fontWeight: 700,
                              display: 'block',
                              mb: 1,
                              textTransform: 'uppercase',
                              letterSpacing: 0.5
                            }}
                          >
                            {campo.etiqueta_display}
                          </Typography>

                          {esImagen && campo.archivo_url ? (
                            <Box
                              sx={{
                                mt: 1,
                                borderRadius: 2,
                                overflow: 'hidden',
                                boxShadow: 2
                              }}
                            >
                              <img
                                src={`${config.API_BASE_URL}${campo.archivo_url}`}
                                alt={campo.etiqueta_display}
                                style={{
                                  width: '100%',
                                  height: 'auto',
                                  maxHeight: 300,
                                  objectFit: 'cover',
                                  display: 'block'
                                }}
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                  e.target.nextSibling.style.display = 'block';
                                }}
                              />
                              <Typography
                                variant="body2"
                                color="error"
                                sx={{ display: 'none', p: 2, textAlign: 'center' }}
                              >
                                No se pudo cargar la imagen
                              </Typography>
                            </Box>
                          ) : esTextoLargo ? (
                            <Paper
                              variant="outlined"
                              sx={{
                                p: 1.5,
                                bgcolor: '#F9FAFB',
                                borderRadius: 1.5,
                                maxHeight: 150,
                                overflow: 'auto'
                              }}
                            >
                              <Typography
                                variant="body2"
                                color="text.primary"
                                sx={{
                                  whiteSpace: 'pre-wrap',
                                  lineHeight: 1.6
                                }}
                              >
                                {campo.valor_campo || '-'}
                              </Typography>
                            </Paper>
                          ) : (
                            <Typography
                              variant="body1"
                              color="text.primary"
                              sx={{
                                fontWeight: 600,
                                fontSize: campo.tipo_campo === 'numero' ? '1.25rem' : '1rem'
                              }}
                            >
                              {campo.tipo_campo === 'numero' && campo.valor_campo ?
                                `${simboloMoneda} ${parseFloat(campo.valor_campo).toLocaleString('es-PE')}` :
                                campo.valor_campo || '-'
                              }
                            </Typography>
                          )}
                        </Paper>
                      </Grid>
                    );
                  })}
                </Grid>
              </Box>
            )}
          </Paper>
        </Box>
      </Box>
    </Box>
  );
};

// Componente DetalleDialog
const DetalleDialog = ({ open, onClose, ordenId }) => {
  const [detalleOrden, setDetalleOrden] = useState(null);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const { obtenerNombreEstado, obtenerColorEstado } = useEstados();
  const { simboloMoneda } = useEmpresa();

  useEffect(() => {
    if (open && ordenId) {
      fetchDetalleOrden();
    }
  }, [open, ordenId]);

  const fetchDetalleOrden = async () => {
    try {
      const response = await axios.get(
        `${config.API_BASE_URL}/api/ordenes/${ordenId}/detalle-completo`,
        {
          headers: { Authorization: `Bearer ${user.token}` }
        }
      );
      setDetalleOrden(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error al obtener detalles:', error);
      setLoading(false);
    }
  };

  if (!open) return null;
  if (loading) return <Dialog open={open}><CircularProgress /></Dialog>;
  if (!detalleOrden) return null;

  const { cabecera, detalles, trabajadores, historial } = detalleOrden;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: '0 24px 48px rgba(0, 0, 0, 0.2)'
        }
      }}
    >
      <DialogTitle
        sx={{
          background: 'linear-gradient(135deg, #1A237E 0%, #283593 100%)',
          color: 'white',
          py: 2.5
        }}
      >
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={2}>
            <CheckCircleIcon sx={{ fontSize: 32 }} />
            <Box>
              <Typography variant="h5" fontWeight="bold">
                Orden #{cabecera.id_orden_servicio}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                {cabecera.tipo_servicio || 'Servicio'} • {cabecera.mes}/{cabecera.anio}
              </Typography>
            </Box>
          </Box>
          <Chip
            label={cabecera.estado_orden}
            sx={{
              bgcolor: 'white',
              color: '#1A237E',
              fontWeight: 'bold',
              fontSize: '0.875rem'
            }}
          />
        </Box>
      </DialogTitle>
      <DialogContent sx={{ p: 3, bgcolor: '#FAFAFA' }}>
        <Grid container spacing={3}>
          {/* Información del Cliente */}
          <Grid item xs={12} md={6}>
            <Paper
              elevation={2}
              sx={{
                p: 3,
                borderRadius: 3,
                background: 'white',
                borderLeft: '4px solid #4FC3F7'
              }}
            >
              <Box display="flex" alignItems="center" gap={1.5} mb={2}>
                <Avatar sx={{ bgcolor: '#4FC3F7', width: 40, height: 40 }}>
                  <CheckCircleIcon />
                </Avatar>
                <Typography variant="h6" fontWeight="bold" color="#1A237E">
                  Información del Cliente
                </Typography>
              </Box>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="body2" color="textSecondary" gutterBottom>
                    Cliente
                  </Typography>
                  <Typography variant="body1" fontWeight="600">
                    {cabecera.nombre_cliente} {cabecera.apellido_cliente}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="textSecondary" gutterBottom>
                    Teléfono
                  </Typography>
                  <Typography variant="body1" fontWeight="600">
                    {cabecera.telefono_cliente}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="textSecondary" gutterBottom>
                    Email
                  </Typography>
                  <Typography variant="body1" fontWeight="600" sx={{ wordBreak: 'break-word' }}>
                    {cabecera.email_cliente}
                  </Typography>
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          {/* Información del Local */}
          <Grid item xs={12} md={6}>
            <Paper
              elevation={2}
              sx={{
                p: 3,
                borderRadius: 3,
                background: 'white',
                borderLeft: '4px solid #FFA726'
              }}
            >
              <Box display="flex" alignItems="center" gap={1.5} mb={2}>
                <Avatar sx={{ bgcolor: '#FFA726', width: 40, height: 40 }}>
                  <RadioButtonCheckedIcon />
                </Avatar>
                <Typography variant="h6" fontWeight="bold" color="#1A237E">
                  Ubicación del Servicio
                </Typography>
              </Box>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="body2" color="textSecondary" gutterBottom>
                    Local
                  </Typography>
                  <Typography variant="body1" fontWeight="600">
                    {cabecera.nombre_local}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2" color="textSecondary" gutterBottom>
                    Dirección
                  </Typography>
                  <Typography variant="body1" fontWeight="600">
                    {cabecera.direccion_local}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2" color="textSecondary" gutterBottom>
                    Período
                  </Typography>
                  <Chip
                    label={`${cabecera.mes}/${cabecera.anio}`}
                    size="small"
                    sx={{ bgcolor: '#FFA726', color: 'white', fontWeight: 'bold' }}
                  />
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          {/* Servicios Solicitados */}
          <Grid item xs={12}>
            <Paper
              elevation={2}
              sx={{
                borderRadius: 3,
                overflow: 'hidden',
                background: 'white'
              }}
            >
              <Box
                sx={{
                  p: 2.5,
                  background: 'linear-gradient(135deg, #66BB6A 0%, #4CAF50 100%)',
                  color: 'white'
                }}
              >
                <Box display="flex" alignItems="center" gap={1.5}>
                  <CheckCircleIcon sx={{ fontSize: 28 }} />
                  <Typography variant="h6" fontWeight="bold">
                    Servicios Solicitados
                  </Typography>
                </Box>
              </Box>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#F5F5F5' }}>
                      <TableCell sx={{ fontWeight: 'bold', color: '#1A237E' }}>Servicio</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', color: '#1A237E' }}>Descripción</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold', color: '#1A237E' }}>Cantidad</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold', color: '#1A237E' }}>Precio Unit.</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold', color: '#1A237E' }}>Subtotal</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', color: '#1A237E' }}>Estado</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {detalles.map((detalle) => (
                      <TableRow
                        key={detalle.id_detalle}
                        sx={{
                          '&:hover': { bgcolor: '#F5F5F5' },
                          transition: 'background-color 0.2s'
                        }}
                      >
                        <TableCell>
                          <Typography variant="body2" fontWeight="600">
                            {detalle.nombre_servicio}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="textSecondary">
                            {detalle.detalle_glosa}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Chip label={detalle.cantidad} size="small" sx={{ fontWeight: 'bold' }} />
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" fontWeight="600">
                            {simboloMoneda} {parseFloat(detalle.precio).toLocaleString('es-PE', { minimumFractionDigits: 2 })}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" fontWeight="bold" color="#1A237E">
                            {simboloMoneda} {(detalle.cantidad * detalle.precio).toLocaleString('es-PE', { minimumFractionDigits: 2 })}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={detalle.estado_servicio}
                            size="small"
                            sx={{
                              bgcolor: '#66BB6A',
                              color: 'white',
                              fontWeight: 'bold'
                            }}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                    {/* Fila de Total */}
                    <TableRow sx={{ bgcolor: '#E3F2FD' }}>
                      <TableCell colSpan={4} align="right">
                        <Typography variant="h6" fontWeight="bold" color="#1A237E">
                          Total:
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="h6" fontWeight="bold" color="#1A237E">
                          {simboloMoneda} {parseFloat(cabecera.precio_total || 0).toLocaleString('es-PE', { minimumFractionDigits: 2 })}
                        </Typography>
                      </TableCell>
                      <TableCell />
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>

          {/* Trabajadores Asignados */}
          {trabajadores.length > 0 && (
            <Grid item xs={12}>
              <Paper
                elevation={2}
                sx={{
                  borderRadius: 3,
                  overflow: 'hidden',
                  background: 'white'
                }}
              >
                <Box
                  sx={{
                    p: 2.5,
                    background: 'linear-gradient(135deg, #7E57C2 0%, #673AB7 100%)',
                    color: 'white'
                  }}
                >
                  <Box display="flex" alignItems="center" gap={1.5}>
                    <Avatar sx={{ bgcolor: 'white', color: '#7E57C2', width: 40, height: 40 }}>
                      <CheckCircleIcon />
                    </Avatar>
                    <Typography variant="h6" fontWeight="bold">
                      Equipo Asignado
                    </Typography>
                  </Box>
                </Box>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow sx={{ bgcolor: '#F5F5F5' }}>
                        <TableCell sx={{ fontWeight: 'bold', color: '#1A237E' }}>Trabajador</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', color: '#1A237E' }}>Contacto</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold', color: '#1A237E' }}>Precio</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold', color: '#1A237E' }}>Comisión %</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold', color: '#1A237E' }}>Comisión {simboloMoneda}</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', color: '#1A237E' }}>Estado</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {trabajadores.map((trabajador) => (
                        <TableRow
                          key={trabajador.id_asignacion}
                          sx={{
                            '&:hover': { bgcolor: '#F5F5F5' },
                            transition: 'background-color 0.2s'
                          }}
                        >
                          <TableCell>
                            <Box display="flex" alignItems="center" gap={1.5}>
                              <Avatar sx={{ bgcolor: '#7E57C2', width: 36, height: 36 }}>
                                {trabajador.nombre_trabajador?.charAt(0)}{trabajador.apellido_trabajador?.charAt(0)}
                              </Avatar>
                              <Typography variant="body2" fontWeight="600">
                                {trabajador.nombre_trabajador} {trabajador.apellido_trabajador}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="textSecondary">
                              📞 {trabajador.telefono_trabajador}
                            </Typography>
                            <Typography variant="body2" color="textSecondary" sx={{ wordBreak: 'break-word' }}>
                              ✉️ {trabajador.email_trabajador}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" fontWeight="600">
                              {simboloMoneda} {parseFloat(trabajador.precio_servicio || 0).toLocaleString('es-PE', { minimumFractionDigits: 2 })}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Chip
                              label={`${trabajador.comision}%`}
                              size="small"
                              sx={{
                                bgcolor: '#E1BEE7',
                                color: '#7E57C2',
                                fontWeight: 'bold'
                              }}
                            />
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" fontWeight="bold" color="#7E57C2">
                              {simboloMoneda} {((trabajador.precio_servicio * trabajador.comision) / 100).toLocaleString('es-PE', { minimumFractionDigits: 2 })}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={trabajador.estado_trabajador}
                              size="small"
                              sx={{
                                bgcolor: '#7E57C2',
                                color: 'white',
                                fontWeight: 'bold'
                              }}
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            </Grid>
          )}

          {/* Historial de Estados - Timeline Mejorado */}
          <Grid item xs={12}>
            <Paper
              elevation={3}
              sx={{
                p: 3,
                borderRadius: 3,
                background: 'linear-gradient(135deg, #FAFAFA 0%, #FFFFFF 100%)'
              }}
            >
              <Box display="flex" alignItems="center" gap={1.5} mb={3}>
                <AccessTimeIcon sx={{ color: '#1A237E', fontSize: 28 }} />
                <Typography variant="h5" fontWeight="bold" color="#1A237E">
                  Línea de Tiempo
                </Typography>
              </Box>

              <Divider sx={{ mb: 3 }} />

              {historial && historial.length > 0 ? (
                <Box sx={{ mt: 2 }}>
                  {historial.map((log, index) => {
                    // Calcular duración en el estado
                    let duracion = null;
                    if (index < historial.length - 1) {
                      const fechaActual = new Date(log.fecha_real);
                      const fechaSiguiente = new Date(historial[index + 1].fecha_real);
                      const diffMs = fechaSiguiente - fechaActual;
                      const diffHoras = Math.floor(diffMs / 3600000);
                      const diffDias = Math.floor(diffHoras / 24);
                      const horasRestantes = diffHoras % 24;

                      if (diffDias > 0) {
                        duracion = `${diffDias}d ${horasRestantes}h`;
                      } else if (diffHoras > 0) {
                        duracion = `${diffHoras}h`;
                      } else {
                        const diffMins = Math.floor(diffMs / 60000);
                        duracion = `${diffMins}min`;
                      }
                    }

                    return (
                      <HistorialItem
                        key={index}
                        log={log}
                        isLast={index === historial.length - 1}
                        duracion={duracion}
                        obtenerColorEstado={obtenerColorEstado}
                        simboloMoneda={simboloMoneda}
                      />
                    );
                  })}
                </Box>
              ) : (
                <Box textAlign="center" py={4}>
                  <Typography variant="body1" color="text.secondary">
                    No hay historial de estados disponible
                  </Typography>
                </Box>
              )}
            </Paper>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions
        sx={{
          borderTop: '2px solid #E0E0E0',
          p: 2.5,
          bgcolor: '#FAFAFA'
        }}
      >
        <Button
          onClick={onClose}
          variant="contained"
          size="large"
          sx={{
            background: 'linear-gradient(135deg, #1A237E 0%, #283593 100%)',
            fontWeight: 'bold',
            px: 4,
            py: 1.5,
            borderRadius: 2,
            '&:hover': {
              background: 'linear-gradient(135deg, #0D47A1 0%, #1565C0 100%)',
              boxShadow: '0 4px 12px rgba(26, 35, 126, 0.4)'
            }
          }}
        >
          Cerrar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DetalleDialog;